import CircledText from 'components/CircledText';
import { observer } from 'mobx-react-lite';
import { IEntryLine } from 'types/Draws';
import { extendClassName } from 'utils/common';
import { colors } from 'utils/constants';
import { shouldShowErrorForNumberChoice } from 'utils/LottoEntryValidator';

interface INewEntryLine {
  id?: string;
  lineIndex: number;
  line: IEntryLine;
  setLineNumber: (line_number: number, position: number, value: number) => void;
  setLineBonusNumber: (
    line_number: number,
    position: number,
    value: number
  ) => void;
  numberRanges: number[];
  bonusNumberRanges: number[];
  useNumberPicker?: boolean;
  onClick?: (idx: number) => void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  glowColor?: string;
}

const NewEntryLine = observer(
  ({
    id,
    lineIndex,
    line,
    setLineNumber,
    setLineBonusNumber,
    numberRanges,
    bonusNumberRanges,
    useNumberPicker = false,
    onClick,
    className,
    style,
    disabled = false,
    glowColor = '#50E3C2',
  }: INewEntryLine) => {
    let lineClassName = 'line';
    if (className) {
      lineClassName += ` ${className}`;
    }

    const rowSize = numberRanges.length + bonusNumberRanges.length || 6;

    if (useNumberPicker) {
      return (
        <div
          className={lineClassName}
          id={id}
          style={style}
        >
          {line.main.map((number, ballIdx) => (
            <CircledText
              text={number?.toString() || '＋'}
              lineColour={colors.primaryBrandGray}
              onClick={!disabled ? () => onClick?.(ballIdx) : undefined}
              key={ballIdx}
            />
          ))}

          {line.bonus.map((number, ballIdx) => (
            <CircledText
              text={number?.toString() || '＋'}
              lineColour={glowColor}
              circleColour={glowColor}
              onClick={!disabled ? () => onClick?.(ballIdx) : undefined}
              key={ballIdx}
            />
          ))}

          <style jsx>
            {`
              :global(.circledText) {
                margin: 1%;
              }

              .line {
                font-size: 1.7em;
                display: flex;
                flex-direction: row;
                flex-grow: 1;
                align-items: stretch;
                justify-content: space-around;
                margin-left: auto;
                margin-right: auto;
                transition: 0.2s;
              }

              :global(.circledText) {
                width: ${100 / rowSize - (2 * 7) / rowSize}%;
                padding-top: ${0.92 * (100 / rowSize - (2 * 7) / rowSize)}%;
                margin: ${7 / rowSize}%;
              }
            `}
          </style>
        </div>
      );
    }

    return (
      <div
        className={lineClassName}
        id={id}
        style={style}
      >
        {line.main.map((number, ballIndex) => (
          <div
            key={ballIndex}
            className={extendClassName(
              'ballOutline flexCentered',
              number !== null &&
                shouldShowErrorForNumberChoice(number, numberRanges[ballIndex])
                ? 'error'
                : ''
            )}
          >
            <input
              placeholder="–"
              disabled={disabled}
              value={number || undefined}
              onChange={(e) =>
                setLineNumber(lineIndex, ballIndex, parseInt(e.target.value))
              }
              className="flexCentered ball"
              min={1}
              max={numberRanges[ballIndex]}
              type="tel"
              maxLength={2}
            />
          </div>
        ))}

        {line.bonus.map((number, ballIndex) => (
          <div
            key={ballIndex}
            className={extendClassName(
              'bonus ballOutline flexCentered',
              number !== null &&
                shouldShowErrorForNumberChoice(
                  number,
                  bonusNumberRanges[ballIndex]
                )
                ? 'error'
                : ''
            )}
          >
            <input
              placeholder="–"
              disabled={disabled}
              value={number || undefined}
              onChange={(e) =>
                setLineBonusNumber(
                  lineIndex,
                  ballIndex,
                  parseInt(e.target.value)
                )
              }
              className="flexCentered ball"
              min={1}
              max={bonusNumberRanges[ballIndex]}
              type="tel"
              maxLength={2}
            />
          </div>
        ))}

        <style jsx>
          {`
            .line {
              font-size: 1.7em;
              display: flex;
              flex-direction: row;
              flex-grow: 1;
              align-items: stretch;
              justify-content: space-around;
              margin-left: auto;
              margin-right: auto;
              transition: 0.2s;
            }

            .ballOutline {
              position: relative;
              width: ${100 / rowSize - (2 * 5) / rowSize}%;
              padding-top: ${0.92 * (100 / rowSize - (2 * 5) / rowSize)}%;
              margin: ${5 / rowSize}%;
              border: 2.5px solid ${colors.primaryBrandGray}25;
              border-radius: 100%;
              overflow: hidden;
            }

            .ball {
              position: absolute;
              top: 0;
              width: 100%;
              height: 100%;
              font-weight: bold;
              transition: 0.2s;
              background-color: transparent;
              color: ${colors.primaryBrandGray};
              text-align: center;
              border: none;
            }

            .ballOutline.bonus {
              border: none;
              background-color: ${colors.primaryBrandGray}25;
            }

            .ballOutline.error {
              border: 3px solid #cb385c;
            }

            input {
              font-size: 0.8em;
            }

            input:focus {
              outline: none;
            }

            @media (min-width: 375px) {
              input {
                font-size: 0.9em;
              }
            }

            @media (min-width: 475px) {
              input {
                font-size: 1.1em;
              }
            }
          `}
        </style>
      </div>
    );
  }
);

export default NewEntryLine;
